"use client";

import React, { useState } from "react";
import Section from "@/components/static/Section";
import Buttons from "@/components/static/Buttons";
import Button from "@/components/static/Button";
import Image from "next/image";
import { SectionProps, ImageProps, ButtonProps } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@relume_io/relume-ui";
import { AnimatePresence, motion } from "framer-motion";

type TabContent = {
    headline: string;
    text: string;
    trigger: string;
    type: string;
    video: any;
    image: ImageProps;
    color: string;
    buttons: ButtonProps[];
    className: string;
};

type Content = {
    topline: string;
    text: string;
    tabs: TabContent[];
    settings: SectionProps;
};

export default function Header_103({ content }: { content: Content }) {
    const [activeTab, setActiveTab] = useState("tab_0");

    return (
        <Section
            dataComponent='Header_103'
            preventAnimation={true}
            settings={{ padding: { top: "none", bottom: "none" }, background: "none" }}
        >
            <div className='relative min-h-screen'>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <AnimatePresence initial={false}>
                        {content.tabs.map(
                            (tab, index) =>
                                "tab_" + index === activeTab && (
                                    <TabsContent
                                        key={index}
                                        value={"tab_" + index}
                                        className='relative max-h-[60rem] min-h-screen overflow-visible'
                                    >
                                        <TabContent
                                            {...tab}
                                            className={cn(
                                                content.tabs.length <= 1 && "justify-end pb-16"
                                            )}
                                        />
                                    </TabsContent>
                                )
                        )}
                    </AnimatePresence>
                    {content.tabs.length > 1 && (
                        <TabsList className='absolute flex flex-row bottom-12 left-0 right-0 top-auto z-20 justify-start gap-4 md:bottom-16 lg:bottom-20 container'>
                            {content.tabs.map((tab, index) => (
                                <TabsTrigger
                                    key={index}
                                    value={"tab_" + index}
                                    onClick={() => setActiveTab("tab_" + index)}
                                    className='relative flex-0 items-start justify-start whitespace-normal w-6 px-0 py-3 border-0 bg-transparent text-neutral duration-0 data-[state=active]:bg-transparent data-[state=active]:text-brand md:min-w-[12.5rem]'
                                >
                                    <span className='hidden md:block'>{tab.trigger}</span>
                                    <div className='absolute inset-0 top-auto h-6 w-6 md:h-1 md:w-full bg-white/20'>
                                        <motion.div
                                            className='h-full bg-brand'
                                            initial={{ width: "0%" }}
                                            animate={{
                                                width: activeTab === "tab_" + index ? "100%" : "0%",
                                            }}
                                            transition={{
                                                duration: activeTab === "tab_" + index ? 1.5 : 0.3,
                                                ...(activeTab === "tab_" + index
                                                    ? {
                                                          type: "spring",
                                                          stiffness: 25,
                                                          damping: 30,
                                                      }
                                                    : { ease: "easeInOut" }),
                                            }}
                                        />
                                    </div>
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    )}
                </Tabs>
            </div>
        </Section>
    );
}

const TabContent = ({ className, ...content }: TabContent) => {
    return (
        <div
            className={cn("flex h-screen flex-col items-start justify-center container", className)}
        >
            <div className=''>
                <motion.div
                    className='flex flex-wrap items-end lg:flex-nowrap gap-x-32 mx-auto'
                    initial={{ y: "20%", opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: "-20%", opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                >
                    <div className='w-full lg:w-[60%]'>
                        <h1 className='text-h1 hyphens-auto text-text-alternative leading-tight sm:hyphens-none'>
                            {content.headline}
                        </h1>
                    </div>
                    <div className='w-full lg:w-[40%] mt-10 lg:mt-0'>
                        {content?.text && <p className='text-white text-md'>{content.text}</p>}
                        {content?.buttons && (
                            <div className='mt-8 flex items-start justify-start gap-x-4 md:mt-8'>
                                <Buttons className='buttons flex-wrap gap-x-8'>
                                    {content?.buttons &&
                                        content?.buttons?.map((button, index) => {
                                            return (
                                                <Button
                                                    as='link'
                                                    href={button?.link?.url}
                                                    type={button?.type}
                                                    key={index}
                                                >
                                                    {button?.link?.title}
                                                </Button>
                                            );
                                        })}
                                </Buttons>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
            <div className='absolute inset-0 -z-10'>
                <div className='absolute inset-0 z-10 bg-black/50' />
                {content.type == "video" && (
                    <video
                        width={content.video.width}
                        height={content.video.height}
                        className='size-full object-cover'
                        autoPlay
                        muted
                        loop
                        playsInline
                    >
                        <source src={content.video.url} type={content.video.mime_type} />
                        Your browser does not support the video tag.
                    </video>
                )}
                {content.type == "image" && (
                    <Image
                        src={content.image.url}
                        alt={content.image.alt}
                        width={content.image.width}
                        height={content.image.height}
                        className='size-full object-cover'
                    />
                )}
                {content.type == "color" && (
                    <div
                        className={cn("size-full object-cover", `bg-[${content.color}]`)}
                        style={{ background: content.color }}
                    ></div>
                )}
                {/* <div className='flex items-center justify-center'>
                    <div className='flex flex-col items-center gap-4'>
                        <div className='w-12 h-20 border-2 border-gray-400 rounded-full relative flex items-center justify-center'>
                            <div className='w-3 h-3 bg-gray-400 rounded-full animate-slide-fade'></div>
                        </div>
                    </div>
                </div> */}
            </div>
        </div>
    );
};
